// src/common/layouts/ProtectedLayout.tsx
import { hasPermission } from '../services/permissionService';
import { getPermissionKeyFromApi } from '../components/fromatPermission';
import { API_ENDPOINTS } from '../../features/dashboard/api/apiEndpoints';
import { API_ENDPOINTS as CLAIM_API } from '../../features/claim/api/apiEndpoints';
import { API_ENDPOINTS as PARTICIPANTS_API } from '../../features/participants/api/apiEndpoints';
import { API_ENDPOINTS as PROVIDERS_API } from '../../features/providers/api/apiEndpoints';
import { API_ENDPOINTS as POLICIES_API } from '../../features/policies/api/apiEndpoints';

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Grid, Layout, Menu, Button, theme, Space, Drawer, Dropdown, Avatar, Input,  AutoComplete, Tooltip} from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  LogoutOutlined,
  // BgColorsOutlined,
  DashboardOutlined,
  MenuOutlined,
  UserOutlined,
  DownOutlined,
  LockOutlined,
  GlobalOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import ThemeEditor from '@/common/components/ThemeEditor/ThemeEditor';
import ThemeColorDropdown from '@/common/components/ThemeColorDropdown';
import DynamicMenu from '@/common/components/DynamicMenu';
import { LogoutConfirmModal } from '@/common/components/LogoutConfirmModal';
import { motion } from 'framer-motion';
import { Resizable } from 're-resizable';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useRouter } from "@tanstack/react-router";
import '@/common/styles/menu-styles.css';



const { Sider, Content, Footer } = Layout; // <-- Add Footer here
const { useBreakpoint } = Grid;
const DEFAULT_SIDER_WIDTH = 210;




const LogoContainer = styled.div`
  height: 72px;
  margin: 0;
  padding: 24px 20px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: 20px;
  font-weight: 800;
  border-bottom: 1px solid var(--theme-border-color);
  background: var(--app-gradient);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
  
  img {
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    filter: brightness(0) invert(1);
    position: relative;
    z-index: 1;
  }
  
  &:hover img {
    transform: scale(1.1) rotate(5deg);
  }
  
  span {
    position: relative;
    z-index: 1;
  }
`;

const StyledHeader = styled(motion.div)`
  &.responsive-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 32px;
    height: 72px;
    background: var(--theme-bg-container);
    border-bottom: 1px solid var(--theme-border-color);
    z-index: 10;
    position: relative;
  }
`;

const ActionButton = styled(Button)`
  border-radius: 12px !important;
  font-weight: 500 !important;
  height: 36px !important;
  padding: 0 14px !important;
  border: 1px solid var(--theme-border-color) !important;
  background: var(--theme-bg-container) !important;
  color: var(--theme-primary-color) !important;
  transition: all 0.2s ease !important;
  font-size: 13px !important;
  
  &:hover {
    border-color: var(--theme-primary-color) !important;
    color: #FFFFFF !important;
    background: var(--theme-primary-color) !important;
    box-shadow: var(--app-shadow-soft) !important;
    transform: translateY(-2px) !important;
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  .anticon {
    font-size: 16px;
  }
  
  &.logout-btn {
    border-color: var(--app-danger-soft-border) !important;
    background: var(--app-danger-soft-bg) !important;
    color: var(--app-danger-soft-text) !important;
    
    &:hover {
      border-color: var(--app-danger-soft-text) !important;
      background: var(--app-danger-soft-bg) !important;
    }
  }
`;

const UserProfileButton = styled(Button)`
  height: 44px !important;
  border-radius: 22px !important;
  padding: 4px 16px 4px 4px !important;
  border: 1px solid var(--theme-border-color) !important;
  background: var(--theme-bg-container) !important;
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  transition: all 0.2s ease !important;
  
  &:hover {
    border-color: var(--theme-primary-color) !important;
    background: var(--theme-bg-hover) !important;
    box-shadow: var(--app-shadow-soft) !important;
  }
  
  .user-name {
    font-weight: 600;
    color: var(--theme-text-color);
    font-size: 14px;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const WelcomeBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid var(--theme-border-color);
  background: var(--theme-bg-hover);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
  min-width: clamp(180px, 22vw, 260px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
    border-color: var(--app-accent-soft-1);
  }

  .welcome-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justifyContent: center;
    font-size: 20px;
    background: var(--app-gradient);
    color: #fff;
    box-shadow: var(--app-shadow-soft);
    flex-shrink: 0;
  }

  .welcome-copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .welcome-label {
    font-size: 11px;
    color: var(--theme-text-color);
    opacity: 0.7;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .user-name-display {
    font-size: 16px;
    font-weight: 700;
    color: var(--theme-text-color);
    letter-spacing: -0.2px;
  }
`;

const PrimaryAction = styled(Button)`
  border-radius: 12px !important;
  height: 38px !important;
  padding: 0 16px !important;
  font-weight: 700 !important;
  border: none !important;
  background: var(--app-gradient) !important;
  color: #ffffff !important;
  box-shadow: var(--app-shadow-soft) !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.2s ease !important;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
`;



const HeaderSearch = styled(Input.Search)`
  width: clamp(220px, 30vw, 340px);
  .ant-input {
    border-radius: 12px;
    padding: 8px 12px;
    background: var(--theme-bg-hover);
    border: 1px solid var(--theme-border-color);
    transition: all 0.2s ease;
  }

  .ant-input:focus {
    border-color: var(--theme-primary-color);
    box-shadow: 0 0 0 3px var(--app-accent-soft-2);
  }

  .ant-input-prefix,
  .ant-input-suffix {
    color: var(--theme-text-color);
    opacity: 0.65;
  }
`;

const StyledContent = styled(Content)`
  margin: 24px;
  padding: 32px;
  min-height: calc(100vh - 72px - 48px - 48px);
  border-radius: 16px;
  background: var(--theme-bg-container);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--app-gradient);
  }
`;


type ProtectedLayoutProps = {
  children: React.ReactNode;
};

function ProtectedLayout({ children }: ProtectedLayoutProps) {

  const router = useRouter();

  const {
    token,
  } = theme.useToken();
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const [themeDrawerVisible, setThemeDrawerVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
 
 
  const screens = useBreakpoint();
  const isPhone = !screens.md;
  const isTabletOrBelow = !screens.lg;
  const [siderWidth, setSiderWidth] = useState(() => {
    const savedWidth = localStorage.getItem('siderWidth');
    return savedWidth ? parseInt(savedWidth, 10) : DEFAULT_SIDER_WIDTH;
  });
  const mobileDrawerWidth = isPhone ? '86vw' : Math.min(siderWidth, 320);

  const handleResizeStop = (
    _e: MouseEvent | TouchEvent,
    _direction: string,
    _ref: HTMLElement,
    d: { width: number; height: number }
  ) => {
    const newWidth = siderWidth + d.width;
    setSiderWidth(newWidth);
    localStorage.setItem('siderWidth', newWidth.toString());
  };

  

  // Scroll window and content to top on page navigation (skip when only a submenu opens,
  // since submenu interactions do not change the pathname).
  // Only scrolls if the user has already scrolled down (position is not at top).
  useEffect(() => {
    if (window.scrollY > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    const el = contentScrollRef.current;
    if (el && el.scrollTop > 0) {
      el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [pathname]);

  const changeLanguage = (lng: string) => {
    setPendingLanguage(lng);
    
    // Try to navigate to trigger unsaved changes check
    // If blocked, user will see modal and can choose to leave
    // If not blocked, we'll change language in the effect
    // setTimeout(() => {
    //   // This will trigger the blocker if forms are dirty
    //   router.navigate({ to: '/__language_change__' }).catch(() => {
    //     // Navigation was blocked or failed, proceed with language change anyway
    //     // since the path doesn't exist, it will fail but trigger the blocker
    //   });
    // }, 50);
  };

  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  // Handle language change after unsaved changes modal (if any)
  useEffect(() => {
    if (pendingLanguage) {
      const timer = setTimeout(() => {
        i18n.changeLanguage(pendingLanguage);
        window.location.reload();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [pendingLanguage, i18n]);

  // const environmentLabel = useMemo(() => {
  //   const raw = (import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'production') as string;
  //   const normalized = raw.toLowerCase();
  //   if (normalized.includes('prod')) return t('env_live');
  //   if (normalized.includes('stage') || normalized.includes('staging') || normalized.includes('test')) return t('env_staging');
  //   return t('env_staging');
  // }, [t]);

// Permission-based recent pages imports (move to top of file)

  const recentPages = useMemo(() => {
    const pages = [];
    if (hasPermission(getPermissionKeyFromApi(API_ENDPOINTS.DASHBOARD.GET_SYSTEM_COUNTS))) {
      pages.push({ key: 'rec-dashboard', label: t('dashboard'), path: '/' });
    }
    if (hasPermission(getPermissionKeyFromApi(CLAIM_API.CLAIM.SEARCH))) {
      pages.push({ key: 'rec-claims', label: t('claims'), path: '/claim/requests' });
    }
    if (hasPermission(getPermissionKeyFromApi(PARTICIPANTS_API.PARTICIPANTS.SEARCH_POLICYHOLDER))) {
      pages.push({ key: 'rec-members', label: t('policyholder'), path: '/participants/policy-holder' });
    }
    if (hasPermission(getPermissionKeyFromApi(PROVIDERS_API.PROVIDERS.SEARCH_MEDICALPROVIDER))) {
      pages.push({ key: 'rec-providers', label: t('Medical Providers'), path: '/providers/medical-provider' });
    }
    if (hasPermission(getPermissionKeyFromApi(POLICIES_API.POLICIES.SEARCH_POLICY))) {
      pages.push({ key: 'rec-policy', label: t('Policies'), path: '/policies/search-policy' });
    }
    return pages;
  }, [t]);

  // const showThemeDrawer = () => {
  //   setThemeDrawerVisible(true);
  // };

  const onCloseThemeDrawer = () => {
    setThemeDrawerVisible(false);
  };

  const menuItems = [
    { key: 'dashboard', icon: <DashboardOutlined />, label: <Link to="/">{t('dashboard')}</Link> },
  ];

  // const workflowSections = useMemo(() => [
  //   {
  //     key: 'operations',
  //     label: t('operations'),
  //     items: [
  //       { key: 'dashboard', icon: <DashboardOutlined />, label: t('dashboard'), path: '/' },
  //     ],
  //   },
  //   {
  //     key: 'claims',
  //     label: t('claims'),
  //     badge: 12,
  //     items: [
  //       { key: 'claims-list', icon: <ProfileOutlined />, label: t('claims'), path: '/claim/list' },
  //     ],
  //   },
  //   {
  //     key: 'members',
  //     label: t('members'),
  //     items: [
  //       { key: 'members-list', icon: <TeamOutlined />, label: t('policyholder'), path: '/customers/policyholders' },
  //     ],
  //   },
  //   {
  //     key: 'providers',
  //     label: t('providers'),
  //     items: [
  //       { key: 'providers-list', icon: <SolutionOutlined />, label: t('healthcare_network'), path: '/providers' },
  //     ],
  //   },
  //   {
  //     key: 'billing',
  //     label: t('billing'),
  //     items: [
  //       { key: 'billing-list', icon: <DollarCircleOutlined />, label: t('billing'), path: '/billing' },
  //     ],
  //   },
  //   {
  //     key: 'reports',
  //     label: t('reports'),
  //     items: [
  //       { key: 'reports-list', icon: <BarChartOutlined />, label: t('reports'), path: '/reports' },
  //     ],
  //   },
  //   {
  //     key: 'admin',
  //     label: t('admin'),
  //     items: [
  //       { key: 'admin-users', icon: <SettingOutlined />, label: t('admin'), path: '/management' },
  //     ],
  //   },
  // ], [t]);

  const userMenuItems: MenuProps['items'] = [
    // {
    //   key: 'theme',
    //   icon: <BgColorsOutlined style={{ fontSize: 16, color: 'var(--app-accent)' }} />,
    //   label: (
    //     <span style={{ fontSize: 14, fontWeight: 500 }}>{t('theme')}</span>
    //   ),
    //   onClick: () => {
    //     showThemeDrawer();
    //   },
    // },
    {
      type: 'divider',
    },
    {
      key: 'change-password',
      icon: <LockOutlined style={{ fontSize: 16, color: 'var(--app-accent)' }} />,
      label: (
        <span style={{ fontSize: 14, fontWeight: 500 }}>{t('Change Password')}</span>
      ),
      onClick: () => {
        // Use setTimeout to ensure dropdown closes first, then navigate
        setTimeout(() => {
          router.navigate({
            to: '/management/users/change-password',
          });
        }, 100);
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined style={{ fontSize: 16, color: 'var(--app-danger-soft-text)' }} />,
      label: (
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--app-danger-soft-text)' }}>
          {t('logout')}
        </span>
      ),
      onClick: () => setLogoutModalVisible(true),
    },
  ];

  // const newActionMenu: MenuProps = useMemo(
  //   () => ({
  //     items: [
  //       {
  //         key: 'new-member',
  //         label: t('new_member'),
  //         onClick: () => router.navigate({ to: '/customers/policyholders/create' }),
  //       },
  //       {
  //         key: 'new-provider',
  //         label: t('new_provider'),
  //         onClick: () => router.navigate({ to: '/providers/create' }),
  //       },
  //       {
  //         key: 'new-policy',
  //         label: t('new_policy'),
  //         onClick: () => router.navigate({ to: '/policies/create' }),
  //       },
  //     ],
  //   }),
  //   [router, t]
  // );

  const searchOptions = useMemo(
    () =>
      recentPages.map((page) => ({
        value: page.path,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{page.label}</span>
            <span style={{ color: token.colorTextSecondary, fontSize: 12 }}>{page.path}</span>
          </div>
        ),
      })),
    [recentPages, token.colorTextSecondary]
  );


  const SiderContent = () => (
    <>
      <LogoContainer>
        <img
          src="/insurance-logo.png"
          alt="Insurance Logo"
          style={{ height: 32, marginRight: 12 }}
        />
        <span style={{ 
          color: '#FFFFFF',
          fontSize: 20,
          fontWeight: 800,
          letterSpacing: '-0.5px'
        }}>
          Insurance
        </span>
      </LogoContainer>
      <div style={{ padding: '16px 0' }}>
        <Menu
          mode="inline"
          defaultSelectedKeys={['dashboard']}
          items={menuItems}
          onClick={() => {
            if (!screens.lg) {
              setMobileMenuVisible(false)
            }
          }}
          style={{
            border: 'none',
            background: 'transparent',
            padding: '0 8px'
          }}
        />
        <DynamicMenu />
      </div>
    </>
  );

  const userName = useAuthStore((state) => state.name); // <-- Get name from store



  return (
    <Layout
      style={{ 
        minHeight: '100vh',
        '--theme-bg-container': token.colorBgContainer,
        '--theme-border-color': token.colorBorder,
        '--theme-primary-color': token.colorPrimary,
        '--theme-text-color': token.colorText,
        '--theme-bg-hover': token.colorBgElevated,
      } as React.CSSProperties}
      dir={dir}
    >
      {screens.lg ? (
        <Tooltip title={`${siderWidth}px`} placement="right">
          <Resizable
            size={{ width: siderWidth, height: '100vh' }}
            onResizeStop={handleResizeStop}
            minWidth={200}
            maxWidth={500}
            enable={{
              top: false,
              right: dir === 'ltr',
              bottom: false,
              left: dir === 'rtl',
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false,
            }}
          >
            <Sider
              dir={dir}
              //collapsible
              collapsed={collapsed}
              onCollapse={setCollapsed}
              style={{ 
                background: token.colorBgContainer, 
               // height: '100vh',
                borderRight: '2px solid var(--app-accent-soft-2)',
                boxShadow: '4px 0 16px rgba(0, 0, 0, 0.04)'
              }}
              width={siderWidth}
            >
              <SiderContent />
            </Sider>
          </Resizable>
        </Tooltip>
      ) : (
        <Drawer
          placement={dir === 'rtl' ? 'right' : 'left'}
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          closable={true}
          width={mobileDrawerWidth}
          bodyStyle={{ padding: 0 }}
        >
          <Sider
            style={{
              height: '100vh',
              background: token.colorBgContainer,
              borderRight: '2px solid var(--app-accent-soft-2)'
            }}
            width={siderWidth}
          >
            <SiderContent />
          </Sider>
        </Drawer>
      )}

      <Layout>
        <StyledHeader
          className="responsive-header"
          style={{
            background: token.colorBgContainer,
            padding: `0 ${screens.xxl ? '32px' : screens.lg ? '20px' : '14px'}`,
            minHeight: screens.xxl ? 72 : 'auto',
            height: screens.xxl ? 72 : 'auto',
            flexWrap: screens.xxl ? 'nowrap' : 'wrap',
            rowGap: screens.xxl ? 0 : 10,
          }}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        >
          {!screens.lg && (
            <ActionButton
              icon={<MenuOutlined style={{ fontSize: 18 }} />}
              onClick={() => setMobileMenuVisible(true)}
              style={{ height: 40, padding: '0 12px' }}
            />
          )}

          
          <WelcomeBox>
            <span className="welcome-icon">👋</span>
            <div className="welcome-copy">
              <span className="welcome-label">{t('Welcome')}</span>
              <span className="user-name-display">{userName || 'User'}</span>
            </div>
          </WelcomeBox>

          <div style={{ flexGrow: 1 }} />

            <AutoComplete
              style={{ marginInlineEnd: 12, flex: '1 1 280px', minWidth: isPhone ? 180 : 240, maxWidth: 360 }}
              options={searchOptions}
              onSelect={(path) => router.navigate({ to: path as string })}
              filterOption={(inputValue, option) =>
                (option?.value as string)?.toLowerCase().includes(inputValue.toLowerCase())
              }
            >
              <HeaderSearch
                placeholder={t('search_command_placeholder')}
                allowClear
                prefix={<SearchOutlined />}
                enterButton={screens.xl ? t('search') : undefined}
                suffix={<span style={{ fontSize: 11, color: token.colorTextSecondary, border: '1px solid var(--theme-border-color)', borderRadius: 8, padding: '2px 6px' }}>Ctrl + K</span>}
              />
            </AutoComplete>

          <Space size={10} wrap align="center">
            
            {hasPermission(getPermissionKeyFromApi(CLAIM_API.CLAIM.SAVE)) && (
              <PrimaryAction onClick={() => router.navigate({ to: '/claim/requests/form' })}>
                {t('new_claim')}
              </PrimaryAction>
            )}
            <ThemeColorDropdown />
            {/* <Dropdown menu={newActionMenu} trigger={['click']} placement="bottomRight">
              <ActionButton icon={<DownOutlined />} />
            </Dropdown> */}

            

            {/* <EnvironmentPill>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: environmentLabel === t('env_live') ? '#22c55e' : '#f59e0b' }} />
              <span>{environmentLabel}</span>
            </EnvironmentPill> */}

            <ActionButton 
              icon={<GlobalOutlined />} 
              onClick={() => changeLanguage(i18n.language === 'ar' ? 'en' : 'ar')}
            >
              {i18n.language === 'ar' ? t('english') : t('arabic')}
            </ActionButton>
            
            <Dropdown 
              menu={{ items: userMenuItems }} 
              placement="bottomRight"
              trigger={['click']}
            >
              <UserProfileButton>
                <Avatar 
                  style={{ 
                    background: 'var(--app-gradient)',
                    flexShrink: 0
                  }} 
                  size={36}
                  icon={<UserOutlined />}
                />
                <span className="user-name">{userName || 'User'}</span>
                <DownOutlined style={{ fontSize: 12, color: token.colorTextSecondary }} />
              </UserProfileButton>
            </Dropdown>
          </Space>
        </StyledHeader>
        <StyledContent
          ref={contentScrollRef}
          style={{
            background: token.colorBgContainer,
            // Remove hardcoded white, use theme token
          }}
        >
          {children}
        </StyledContent>
        <Footer style={{ textAlign: 'center' }}>
                  {/*© {new Date().getFullYear()} Insurance System. All rights reserved.*/}
                  {t("footerText", { year: new Date().getFullYear() })}
        </Footer>
      </Layout>

      <Drawer
        title={t('theme_settings')}
        placement={dir === 'rtl' ? 'left' : 'right'}
        onClose={onCloseThemeDrawer}
        open={themeDrawerVisible}
        mask={false}
      >
        <ThemeEditor />
      </Drawer>

      <LogoutConfirmModal
        open={logoutModalVisible}
        onCancel={() => setLogoutModalVisible(false)}
        onConfirm={() => {
          setLogoutModalVisible(false);
          logout();
        }}
      />
    </Layout>
  );
}

export default ProtectedLayout;
